import { Injectable } from '@angular/core';
import { AppTagItemEntity } from '@datachain/ui-sdk/components/dc-types';
import { OrderedMap, OrderedSet } from 'immutable';

import { MetadataItemEntity } from '../../metadata/components/metadata-list/metadata-item.entity';
import { MetadataListEntity } from '../../metadata/components/metadata-list/metadata-list.entity';
import {
	IDcMetadataResponse,
	IMetadataTag,
} from '../../requesters/core/models/metadata.model';
import { AppMetadataEntity } from '../../ui/components/app-metadata/app-metadata.entity';
import { DcCoreElementType, DcCoreElementTypesMapping } from '../dc-core-types';
import { ProjectStorage } from '../services/project.storage';
import { DcRouteUrlBuilder } from '../utils/dc-route-url-builder';
import { TagAdapter } from './tag.adapter';

@Injectable({
	providedIn: 'root',
})
export class MetadataAdapter {
	public constructor(
		private readonly tagResponseAdapter: TagAdapter,
		private readonly projectStorage: ProjectStorage
	) {}

	public generateMetadata(
		response: IDcMetadataResponse | undefined
	): AppMetadataEntity {
		if (response === undefined) {
			throw new Error('no data found to parse');
		}
		return AppMetadataEntity.build({
			title: response?.label ?? '',
			id: response?.id ?? -1,
			description: response?.description ?? '',
			isActive: response?.actif ?? false,
			code: response?.code ?? '',
			createdBy: response?.created_by ?? '',
			updatedBy: response?.updated_by ?? '',
			updateDate: response?.update_date ?? -1,
			creationDate: response?.creation_date ?? -1,
			tags: this.generateMetaDataTags(response.tags),
		});
	}

	public generateMetadataListEntity(
		response: ReadonlyArray<IDcMetadataResponse>
	): MetadataListEntity {
		const list = MetadataListEntity.build([
			{
				field: 'type',
				alias: $localize`:i18n=@@metadata.list.column.type:`,
				type: 'string',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: true,
				visibleIndex: 0,
			},
			{
				field: 'code',
				alias: $localize`:i18n=@@metadata.list.column.code:`,
				type: 'string',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: true,
				visible: false,
				visibleIndex: 1,
			},
			{
				field: 'label',
				alias: $localize`:i18n=@@metadata.list.column.label:`,
				type: '',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: true,
				visibleIndex: 2,
			},
			{
				field: 'description',
				alias: $localize`:i18n=@@metadata.list.column.description:`,
				type: '',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: true,
				visibleIndex: 3,
			},
			{
				field: 'tags',
				alias: $localize`:i18n=@@metadata.list.column.tags:`,
				type: '',
				isSearchable: false,
				isFilterable: false,
				hasHeaderFilter: false,
				visibleIndex: 4,
			},
		]);
		const tuples = response.reduce((acc, curr) => {
			const entity = this.generateMetadataItemEntity(curr);
			acc.push([`${curr.id}`, entity]);
			return acc;
		}, new Array<[string, MetadataItemEntity]>());
		return list.setElements(OrderedMap(tuples));
	}

	private generateMetaDataTags(
		tags: ReadonlyArray<IMetadataTag> | undefined
	): OrderedSet<AppTagItemEntity> {
		if (tags === undefined) {
			return OrderedSet();
		}
		return this.tagResponseAdapter.generateResponseMetadataTag(tags);
	}

	private generateMetadataItemEntity(
		response: IDcMetadataResponse
	): MetadataItemEntity {
		const metadata = {
			title: response?.label ?? '',
			id: response?.id ?? -1,
			description: response?.description ?? '',
			isActive: response?.actif ?? false,
			code: response?.code ?? '',
			tags: this.generateMetaDataTags(response.tags),
			type: response?.hdh_type
				? DcCoreElementTypesMapping[response?.hdh_type]
				: DcCoreElementType.UNKNOWN,
		};
		return MetadataItemEntity.build({
			...metadata,
			sourceUrl: this.parseSourcePageUrl({
				id: metadata.id,
				type: metadata.type,
				subType: '',
			}),
		});
	}

	public parseSourcePageUrl(metadata: {
		id: number;
		type: DcCoreElementType;
		subType: string;
	}): string {
		const projectId = this.projectStorage.getValue()?.projectId;
		return DcRouteUrlBuilder.buildEditPageUrl(
			projectId,
			metadata.id,
			metadata.type,
			metadata.subType
		);
	}
}
